import { useState, useEffect, useCallback } from 'react';
import { api, githubApi, GitHubRepo } from '../services/api';

interface GitStatus {
  modified: string[];
  added: string[];
  deleted: string[];
  untracked: string[];
  staged: string[];
  clean: boolean;
  currentBranch: string;
}

interface GitCommit {
  hash: string;
  shortHash: string;
  message: string;
  author: string;
  date: string;
  insertions: number;
  deletions: number;
}

interface GitDiff {
  file: string;
  additions: number;
  deletions: number;
  hunks: {
    oldStart: number;
    newStart: number;
    lines: { type: string; content: string }[];
  }[];
}

interface GitBranch {
  name: string;
  isCurrent: boolean;
}

export default function GitPage() {
  const [activeTab, setActiveTab] = useState<'changes' | 'commits' | 'branches' | 'diff' | 'github'>('changes');
  const [repoPath, setRepoPath] = useState('/opt/apps/halcode-webapp/repos/demo');
  const [status, setStatus] = useState<GitStatus | null>(null);
  const [commits, setCommits] = useState<GitCommit[]>([]);
  const [branches, setBranches] = useState<GitBranch[]>([]);
  const [selectedCommit, setSelectedCommit] = useState<GitCommit | null>(null);
  const [diff, setDiff] = useState<GitDiff[]>([]);
  const [commitMessage, setCommitMessage] = useState('');
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // GitHub state
  const [githubRepos, setGithubRepos] = useState<GitHubRepo[]>([]);
  const [githubLoading, setGithubLoading] = useState(false);
  const [cloneStatus, setCloneStatus] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/git/status', { params: { repoPath } });
      setStatus(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  }, [repoPath]);

  const fetchCommits = useCallback(async () => {
    try {
      const response = await api.get('/git/commits', { params: { repoPath, limit: 20 } });
      setCommits(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  }, [repoPath]);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await api.get('/git/branches', { params: { repoPath } });
      setBranches(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  }, [repoPath]);

  const fetchDiff = useCallback(async (commitHash?: string) => {
    try {
      const response = await api.get('/git/diff', { 
        params: { repoPath, commit: commitHash } 
      });
      setDiff(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  }, [repoPath]);

  const generateCommitMessage = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/git/generate-commit-message', null, { params: { repoPath } });
      setGeneratedMessage(response.data.message);
      setCommitMessage(response.data.message);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStage = async (files: string[]) => {
    try {
      await api.post('/git/stage', { files }, { params: { repoPath } });
      fetchStatus();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUnstage = async (files: string[]) => {
    try {
      await api.post('/git/unstage', { files }, { params: { repoPath } });
      fetchStatus();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCommit = async () => {
    if (!commitMessage.trim()) return;
    
    try {
      await api.post('/git/commit', { message: commitMessage }, { params: { repoPath } });
      setCommitMessage('');
      setGeneratedMessage('');
      fetchStatus();
      fetchCommits();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handlePush = async () => {
    try {
      await api.post('/git/push', null, { params: { repoPath } });
      fetchStatus();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handlePull = async () => {
    try {
      await api.post('/git/pull', {}, { params: { repoPath } });
      fetchStatus();
      fetchCommits();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCheckout = async (branchName: string) => {
    try {
      await api.post('/git/checkout', { branchName }, { params: { repoPath } });
      fetchStatus();
      fetchBranches();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const viewCommitDiff = async (commit: GitCommit) => {
    setSelectedCommit(commit);
    setActiveTab('diff');
    await fetchDiff(commit.hash);
  };

  useEffect(() => {
    fetchStatus();
    fetchCommits();
    fetchBranches();
  }, [fetchStatus, fetchCommits, fetchBranches]);

  const getFileIcon = (file: string) => {
    const ext = file.split('.').pop()?.toLowerCase();
    const icons: Record<string, string> = {
      ts: '📘', tsx: '📘', js: '📗', jsx: '📗',
      py: '🐍', java: '☕', go: '🐹',
      html: '🌐', css: '🎨', json: '📋',
      md: '📝', txt: '📄', gitignore: '🚫'
    };
    return icons[ext || ''] || '📄';
  };

  const getStatusColor = (type: string) => {
    switch (type) {
      case 'modified': return 'text-yellow-500';
      case 'added': return 'text-green-500';
      case 'deleted': return 'text-red-500';
      case 'untracked': return 'text-gray-400';
      default: return 'text-gray-500';
    }
  };

  // GitHub functions - Add these functions here
  const fetchGithubRepos = async () => {
    setGithubLoading(true);
    setError(null);
    try {
      const response = await githubApi.getRepos();
      setGithubRepos(response.data);
      setActiveTab('github');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load GitHub repos. Make sure GitHub is connected in Settings.');
    } finally {
      setGithubLoading(false);
    }
  };

  const handleCloneRepo = async (repo: GitHubRepo) => {
    setGithubLoading(true);
    setCloneStatus(`Cloning ${repo.fullName}...`);
    setError(null);
    try {
      const response = await githubApi.clone(repo.cloneUrl, repo.name);
      setRepoPath(response.data.path);
      setCloneStatus(null);
      setActiveTab('changes');
      await fetchStatus();
      await fetchCommits();
      await fetchBranches();
    } catch (err: any) {
      setCloneStatus(null);
      setError(err.response?.data?.error || 'Failed to clone repository');
    } finally {
      setGithubLoading(false);
    }
  };

  const handleDownloadRepo = async () => {
    if (!repoPath) return;
    try {
      const response = await api.get('/git/download', {
        params: { repoPath },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const repoName = repoPath.split('/').pop() || 'repository';
      link.setAttribute('download', `${repoName}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to download repository');
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold">Git Operations</h1>
          
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-sm">Branch:</span>
            <span className="px-2 py-1 bg-blue-600 rounded text-sm font-mono">
              {status?.currentBranch || '...'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadRepo}
            className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download ZIP
          </button>
          
          <button
            onClick={handlePull}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Pull
          </button>
          
          <button
            onClick={handlePush}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
            Push
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="px-4 py-2 bg-red-600/20 border-b border-red-600 text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Tabs */}
        <div className="w-48 bg-gray-800 border-r border-gray-700 flex flex-col">
          <button
            onClick={() => setActiveTab('changes')}
            className={`px-4 py-3 text-left text-sm font-medium flex items-center gap-2 ${
              activeTab === 'changes' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Changes
          </button>
          
          <button
            onClick={() => setActiveTab('commits')}
            className={`px-4 py-3 text-left text-sm font-medium flex items-center gap-2 ${
              activeTab === 'commits' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            History
          </button>
          
          <button
            onClick={() => setActiveTab('branches')}
            className={`px-4 py-3 text-left text-sm font-medium flex items-center gap-2 ${
              activeTab === 'branches' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Branches
          </button>
          
          <button
            onClick={() => { setActiveTab('diff'); fetchDiff(); }}
            className={`px-4 py-3 text-left text-sm font-medium flex items-center gap-2 ${
              activeTab === 'diff' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
            </svg>
            Diff
          </button>
          
          <button
            onClick={fetchGithubRepos}
            disabled={githubLoading}
            className={`px-4 py-3 text-left text-sm font-medium flex items-center gap-2 ${
              activeTab === 'github' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
            } ${githubLoading ? 'opacity-50' : ''}`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            GitHub
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'changes' && (
            <div className="space-y-6">
              {/* Staged Changes */}
              {status?.staged && status.staged.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    Staged Changes
                  </h3>
                  <div className="bg-gray-800 rounded-lg overflow-hidden">
                    {status.staged.map((file, index) => (
                      <div key={index} className="px-4 py-2 flex items-center justify-between border-b border-gray-700 last:border-0">
                        <span className="flex items-center gap-2">
                          <span>{getFileIcon(file)}</span>
                          <span className="font-mono text-sm">{file}</span>
                        </span>
                        <button
                          onClick={() => handleUnstage([file])}
                          className="text-xs text-yellow-400 hover:text-yellow-300"
                        >
                          Unstage
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unstaged Changes */}
              {((status?.modified?.length ?? 0) > 0 || (status?.added?.length ?? 0) > 0) && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                    Unstaged Changes
                  </h3>
                  <div className="bg-gray-800 rounded-lg overflow-hidden">
                    {(status?.modified || []).map((file, index) => (
                      <div key={`mod-${index}`} className="px-4 py-2 flex items-center justify-between border-b border-gray-700 last:border-0">
                        <span className="flex items-center gap-2">
                          <span className="text-yellow-500">M</span>
                          <span>{getFileIcon(file)}</span>
                          <span className="font-mono text-sm">{file}</span>
                        </span>
                        <button
                          onClick={() => handleStage([file])}
                          className="text-xs text-green-400 hover:text-green-300"
                        >
                          Stage
                        </button>
                      </div>
                    ))}
                    {(status?.added || []).map((file, index) => (
                      <div key={`add-${index}`} className="px-4 py-2 flex items-center justify-between border-b border-gray-700 last:border-0">
                        <span className="flex items-center gap-2">
                          <span className="text-green-500">A</span>
                          <span>{getFileIcon(file)}</span>
                          <span className="font-mono text-sm">{file}</span>
                        </span>
                        <button
                          onClick={() => handleStage([file])}
                          className="text-xs text-green-400 hover:text-green-300"
                        >
                          Stage
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Untracked Files */}
              {status?.untracked && status.untracked.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-3 h-3 bg-gray-500 rounded-full"></span>
                    Untracked Files
                  </h3>
                  <div className="bg-gray-800 rounded-lg overflow-hidden">
                    {status.untracked.map((file, index) => (
                      <div key={index} className="px-4 py-2 flex items-center justify-between border-b border-gray-700 last:border-0">
                        <span className="flex items-center gap-2">
                          <span>{getFileIcon(file)}</span>
                          <span className="font-mono text-sm">{file}</span>
                        </span>
                        <button
                          onClick={() => handleStage([file])}
                          className="text-xs text-green-400 hover:text-green-300"
                        >
                          Track
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Commit Section */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3">Commit Changes</h3>
                
                <div className="space-y-3">
                  <div>
                    <textarea
                      value={commitMessage}
                      onChange={(e) => setCommitMessage(e.target.value)}
                      placeholder="Enter commit message..."
                      className="w-full px-3 py-2 bg-gray-700 rounded border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                      rows={3}
                    />
                  </div>
                  
                  {generatedMessage && (
                    <div className="p-2 bg-blue-600/20 border border-blue-600 rounded text-sm">
                      <span className="text-blue-400">AI Suggestion: </span>
                      {generatedMessage}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3">
                    <button
                      onClick={generateCommitMessage}
                      disabled={isLoading || status?.clean}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 rounded text-sm flex items-center gap-2"
                    >
                      {isLoading ? (
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      )}
                      Generate Message
                    </button>
                    
                    <button
                      onClick={handleCommit}
                      disabled={!commitMessage.trim() || status?.clean}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded text-sm flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Commit
                    </button>
                  </div>
                </div>
              </div>

              {status?.clean && (
                <div className="text-center py-8 text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p>Working directory is clean</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'commits' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Commit History</h2>
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                {commits.map((commit) => (
                  <div
                    key={commit.hash}
                    onClick={() => viewCommitDiff(commit)}
                    className="px-4 py-3 border-b border-gray-700 last:border-0 hover:bg-gray-700 cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm text-yellow-500">{commit.shortHash}</span>
                          <span className="text-white font-medium">{commit.message}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>{commit.author}</span>
                          <span>{new Date(commit.date).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-500">+{commit.insertions}</span>
                        <span className="text-red-500">-{commit.deletions}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'branches' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Branches</h2>
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                {branches.map((branch, index) => (
                  <div
                    key={index}
                    className={`px-4 py-3 border-b border-gray-700 last:border-0 flex items-center justify-between ${
                      branch.isCurrent ? 'bg-gray-700' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {branch.isCurrent && (
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      )}
                      <span className="font-mono">{branch.name}</span>
                    </div>
                    {!branch.isCurrent && (
                      <button
                        onClick={() => handleCheckout(branch.name)}
                        className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded"
                      >
                        Checkout
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'diff' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {selectedCommit ? `Changes in ${selectedCommit.shortHash}` : 'Working Directory Diff'}
                </h2>
              </div>
              
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                {diff.length > 0 ? diff.map((fileDiff, index) => (
                  <div key={index} className="border-b border-gray-700 last:border-0">
                    <div className="px-4 py-2 bg-gray-700 flex items-center justify-between">
                      <span className="font-mono text-sm">{fileDiff.file}</span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-500">+{fileDiff.additions}</span>
                        <span className="text-red-500">-{fileDiff.deletions}</span>
                      </div>
                    </div>
                    <div className="p-4 font-mono text-sm overflow-x-auto">
                      {fileDiff.hunks.map((hunk, hunkIndex) => (
                        <div key={hunkIndex} className="mb-2">
                          <div className="text-gray-500 text-xs mb-1">
                            @@ -{hunk.oldStart} @@
                          </div>
                          {hunk.lines.map((line, lineIndex) => (
                            <div
                              key={lineIndex}
                              className={`px-2 ${
                                line.type === 'addition'
                                  ? 'bg-green-900/30 text-green-400'
                                  : line.type === 'deletion'
                                  ? 'bg-red-900/30 text-red-400'
                                  : 'text-gray-400'
                              }`}
                            >
                              {line.type === 'addition' ? '+' : line.type === 'deletion' ? '-' : ' '}
                              {line.content}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )) : (
                  <div className="p-8 text-center text-gray-400">
                    No changes to display
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* GitHub Repositories Tab */}
          {activeTab === 'github' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">GitHub Repositories</h2>
              
              {cloneStatus && (
                <div className="p-4 bg-blue-600/20 border border-blue-600 rounded-lg text-blue-400">
                  {cloneStatus}
                </div>
              )}
              
              {githubRepos.length === 0 ? (
                <div className="p-8 text-center">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  <p className="text-gray-400 mb-4">No repositories loaded</p>
                  <button
                    onClick={fetchGithubRepos}
                    disabled={githubLoading}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded text-white"
                  >
                    {githubLoading ? 'Loading...' : 'Load Your Repositories'}
                  </button>
                  <p className="text-gray-500 text-sm mt-4">
                    Make sure GitHub is connected in Settings first.
                  </p>
                </div>
              ) : (
                <div className="bg-gray-800 rounded-lg overflow-hidden">
                  {githubRepos.map((repo) => (
                    <div 
                      key={repo.id} 
                      className="px-4 py-3 border-b border-gray-700 last:border-0 hover:bg-gray-700"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white">{repo.name}</span>
                            {repo.private && (
                              <span className="text-xs px-2 py-0.5 bg-gray-600 text-gray-300 rounded">Private</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-400">{repo.fullName}</div>
                          {repo.description && (
                            <div className="text-sm text-gray-500 mt-1">{repo.description}</div>
                          )}
                        </div>
                        <button
                          onClick={() => handleCloneRepo(repo)}
                          disabled={githubLoading}
                          className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded text-sm text-white ml-4"
                        >
                          {githubLoading ? 'Cloning...' : 'Clone'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="p-4 bg-gray-800 rounded-lg">
                <h3 className="font-medium text-white mb-2">Using GitHub Integration</h3>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• Click "Load Your Repositories" to fetch your GitHub repos</li>
                  <li>• Click "Clone" to clone a repository to your workspace</li>
                  <li>• Once cloned, you can use all Git operations (commit, push, pull)</li>
                  <li>• Changes will be synced to your GitHub repository</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
